import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar as CalendarIcon, MapPin, Search, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { format } from "date-fns";

const TIMES = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, "0")}:00`);

export const SearchBar = ({ variant = "dark" }) => {
  const nav = useNavigate();
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState("bangalore");
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  const dayAfter = new Date(); dayAfter.setDate(dayAfter.getDate() + 2);
  const [pickup, setPickup] = useState(tomorrow);
  const [drop, setDrop] = useState(dayAfter);
  const [pickupTime, setPickupTime] = useState("10:00");
  const [dropTime, setDropTime] = useState("10:00");

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
  }, []);

  const handleSearch = () => {
    const params = new URLSearchParams({
      city,
      pickup_date: pickup.toISOString().split("T")[0],
      pickup_time: pickupTime,
      drop_date: drop.toISOString().split("T")[0],
      drop_time: dropTime,
    });
    nav(`/bikes?${params.toString()}`);
  };

  const isDark = variant === "dark";
  const fieldBase = isDark
    ? "bg-white/5 hover:bg-white/10 border-white/10 text-white"
    : "bg-white hover:bg-gray-50 border-gray-200 text-ink";

  return (
    <div className={`${isDark ? "glass-dark" : "bg-white border border-gray-200 shadow-2xl shadow-black/5"} rounded-3xl p-3 md:p-4`} data-testid="hero-search-bar">
      <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1.5fr_1.5fr_auto] gap-2 items-stretch">
        {/* City */}
        <div className={`relative rounded-2xl border ${fieldBase} px-4 py-3 transition-colors`}>
          <label className={`text-[10px] uppercase tracking-widest font-bold ${isDark ? "text-white/50" : "text-gray-500"}`}>City</label>
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger data-testid="search-city-select" className="border-0 bg-transparent shadow-none p-0 h-7 text-base font-semibold focus:ring-0">
              <div className="flex items-center gap-2">
                <MapPin size={15} className={isDark ? "text-[#3DDC97]" : "text-[#2D7FF9]"} />
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent>
              {cities.map((c) => (
                <SelectItem key={c.slug} value={c.slug} data-testid={`city-option-${c.slug}`}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Pickup */}
        <div className={`relative rounded-2xl border ${fieldBase} px-4 py-3 transition-colors`}>
          <label className={`text-[10px] uppercase tracking-widest font-bold ${isDark ? "text-white/50" : "text-gray-500"}`}>Pickup</label>
          <div className="flex items-center gap-1 mt-0.5">
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="pickup-date-btn" className="flex items-center gap-1.5 text-sm font-semibold">
                  <CalendarIcon size={14} className={isDark ? "text-[#3DDC97]" : "text-[#2D7FF9]"} />
                  {format(pickup, "dd MMM")}
                  <ChevronDown size={12} className="opacity-60" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0" align="start">
                <Calendar mode="single" selected={pickup} onSelect={(d) => d && setPickup(d)} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} />
              </PopoverContent>
            </Popover>
            <span className={isDark ? "text-white/30" : "text-gray-300"}>·</span>
            <Select value={pickupTime} onValueChange={setPickupTime}>
              <SelectTrigger data-testid="pickup-time-select" className="border-0 bg-transparent shadow-none p-0 h-7 w-auto text-sm font-semibold focus:ring-0 gap-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Drop */}
        <div className={`relative rounded-2xl border ${fieldBase} px-4 py-3 transition-colors`}>
          <label className={`text-[10px] uppercase tracking-widest font-bold ${isDark ? "text-white/50" : "text-gray-500"}`}>Drop</label>
          <div className="flex items-center gap-1 mt-0.5">
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="drop-date-btn" className="flex items-center gap-1.5 text-sm font-semibold">
                  <CalendarIcon size={14} className={isDark ? "text-[#3DDC97]" : "text-[#2D7FF9]"} />
                  {format(drop, "dd MMM")}
                  <ChevronDown size={12} className="opacity-60" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0" align="start">
                <Calendar mode="single" selected={drop} onSelect={(d) => d && setDrop(d)} disabled={(d) => d <= pickup} />
              </PopoverContent>
            </Popover>
            <span className={isDark ? "text-white/30" : "text-gray-300"}>·</span>
            <Select value={dropTime} onValueChange={setDropTime}>
              <SelectTrigger data-testid="drop-time-select" className="border-0 bg-transparent shadow-none p-0 h-7 w-auto text-sm font-semibold focus:ring-0 gap-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <button onClick={handleSearch} data-testid="hero-search-btn" className="btn-primary justify-center !rounded-2xl !px-7 !py-4">
          <Search size={18} />
          <span>Search</span>
        </button>
      </div>
    </div>
  );
};
